import { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { OrganizationInvitationDetails } from "../shared/Interfaces";
import { Button } from "../components/ui/button";
import { reportApiError, reportHandledError } from "../utils/reportError";

const getRoleDisplay = (role: string): string => {
    const roleLower = (role || '').toLowerCase();
    switch (roleLower) {
        case 'owner': return '👑 Proprietário';
        case 'admin': return '⚡ Admin';
        case 'member': return '👤 Membro';
        default: return '👤 Membro';
    }
};

type InvitationAction = "accept" | "decline";
type InvitationMismatchInfo = {
    inviteeEmail?: string;
    organizationId?: string;
    organizationName?: string;
};

function OrganizationInvitePage() {
    const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();
    const location = useLocation();
    const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
    const [loadingInvite, setLoadingInvite] = useState(true);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [inviteDetails, setInviteDetails] = useState<OrganizationInvitationDetails | null>(null);
    const [processingAction, setProcessingAction] = useState(false);
    const [actionCompleted, setActionCompleted] = useState<InvitationAction | null>(null);
    const [actionStatus, setActionStatus] = useState<string | null>(null);
    const [inviteMismatchInfo, setInviteMismatchInfo] = useState<InvitationMismatchInfo | null>(null);
    const [inviteMismatchDetected, setInviteMismatchDetected] = useState(false);

    const token = useMemo(() => new URLSearchParams(location.search).get("token"), [location.search]);

    useEffect(() => {
        if (!token) {
            setInviteError("Convite inválido ou ausente.");
            setLoadingInvite(false);
            return;
        }

        if (!isLoading && !isAuthenticated && !hasAttemptedLogin) {
            setHasAttemptedLogin(true);
            loginWithRedirect({
                appState: { returnTo: `/organizacao-convite?token=${token}` },
            });
        }
    }, [token, isLoading, isAuthenticated, loginWithRedirect, hasAttemptedLogin]);

    useEffect(() => {
        if (!token || !isAuthenticated) return;

        let canceled = false;
        const fetchDetails = async () => {
            setLoadingInvite(true);
            setInviteError(null);
            setInviteMismatchInfo(null);
            setInviteDetails(null);
            setInviteMismatchDetected(false);
            try {
                const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
                if (!accessToken) return;
                const response = await fetch(`${API_BASE}/api/organizations/invitations/${token}`, {
                    method: "GET",
                    headers: { Authorization: `Auth ${accessToken}` },
                });

                if (!response.ok) {
                    reportApiError(response, { feature: "invite.fetch", method: "GET" });
                    const errorPayload = await response.text();
                    let detail: string | undefined;
                    let mismatchInfo: InvitationMismatchInfo | null = null;
                    try {
                        const parsed = JSON.parse(errorPayload);
                        const parsedDetail = parsed.detail;
                        if (typeof parsedDetail === "string") {
                            detail = parsedDetail;
                        } else if (parsedDetail && typeof parsedDetail === "object") {
                            detail = parsedDetail.message || parsedDetail.detail;
                            mismatchInfo = {
                                inviteeEmail: parsedDetail.invitee_email,
                                organizationId: parsedDetail.organization_id,
                                organizationName: parsedDetail.organization_name,
                            };
                        }
                    } catch (_e) {
                        detail = undefined;
                    }
                    if (!canceled) {
                        if (response.status === 403) {
                            setInviteMismatchDetected(true);
                            setInviteMismatchInfo(mismatchInfo);
                            setInviteError(detail || "Este convite é destinado a outro e-mail. Faça login com a conta correta.");
                        } else if (response.status === 410) {
                            setInviteError(detail || "Este convite expirou.");
                        } else if (response.status === 404) {
                            setInviteError(detail || "Convite não encontrado.");
                        } else {
                            setInviteError(detail || "Não foi possível carregar o convite.");
                        }
                    }
                    return;
                }

                const data: OrganizationInvitationDetails = await response.json();
                if (!canceled) {
                    setInviteMismatchInfo(null);
                    setInviteMismatchDetected(false);
                    setInviteDetails(data);
                }
            } catch (_error) {
                reportHandledError(_error, { category: "api", feature: "invite.fetch" });
                if (!canceled) {
                    setInviteError("Não foi possível carregar o convite.");
                }
            } finally {
                if (!canceled) {
                    setLoadingInvite(false);
                }
            }
        };

        fetchDetails();
        return () => { canceled = true; };
    }, [token, isAuthenticated, getAccessTokenSilently]);

    const showLoginPrompt = !isLoading && !isAuthenticated && token;

    const formatLocalizedDateTime = (value: string) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString("pt-BR", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    };

    const handleAction = async (action: InvitationAction) => {
        if (!token || actionCompleted) return;

        setProcessingAction(true);
        try {
            const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
            if (!accessToken) return;
            const response = await fetch(`${API_BASE}/api/organizations/invitations/${token}/${action}`, {
                method: "POST",
                headers: { Authorization: `Auth ${accessToken}` },
            });

            if (!response.ok) {
                reportApiError(response, { feature: "invite.action", method: "POST" });
                const errorPayload = await response.text();
                let detailMessage: string | undefined;
                let mismatchPayload: InvitationMismatchInfo | null = null;
                try {
                    const parsed = JSON.parse(errorPayload);
                    const parsedDetail = parsed.detail;
                    if (typeof parsedDetail === "string") {
                        detailMessage = parsedDetail;
                    } else if (parsedDetail && typeof parsedDetail === "object") {
                        detailMessage = parsedDetail.message || parsedDetail.detail;
                        mismatchPayload = {
                            inviteeEmail: parsedDetail.invitee_email,
                            organizationId: parsedDetail.organization_id,
                            organizationName: parsedDetail.organization_name,
                        };
                    }
                } catch (_e) {
                    detailMessage = undefined;
                }
                if (response.status === 403) {
                    setInviteMismatchDetected(true);
                    if (mismatchPayload) {
                        setInviteMismatchInfo(mismatchPayload);
                    }
                }
                toast.error(detailMessage || "Não foi possível atualizar o convite.", TOASTPROPS);
                return;
            }

            const data = await response.json();
            const message = data.message ||
                (action === "accept" ? "Convite aceito com sucesso." : "Convite recusado com sucesso.");

            toast.success(message, TOASTPROPS);
            setActionStatus(message);
            setActionCompleted(action);
        } catch (_error) {
            reportHandledError(_error, { category: "api", feature: "invite.action" });
            toast.error("Não foi possível atualizar o convite.", TOASTPROPS);
        } finally {
            setProcessingAction(false);
        }
    };

    const handleGoToPlatform = () => { window.location.href = "/"; };

    const handleLogin = () => {
        if (!token) { loginWithRedirect(); return; }
        loginWithRedirect({ appState: { returnTo: `/organizacao-convite?token=${token}` } });
    };

    const handleSwitchAccount = () => {
        if (!token) {
            loginWithRedirect({
                authorizationParams: {
                    prompt: "login" as const,
                    ...(inviteMismatchInfo?.inviteeEmail ? { login_hint: inviteMismatchInfo.inviteeEmail } : {}),
                },
            });
            return;
        }
        loginWithRedirect({
            appState: { returnTo: `/organizacao-convite?token=${token}` },
            authorizationParams: {
                prompt: "login" as const,
                ...(inviteMismatchInfo?.inviteeEmail ? { login_hint: inviteMismatchInfo.inviteeEmail } : {}),
            },
        });
    };

    const inviteeEmail = inviteDetails?.invitee_email;
    const isAuthenticatedWithInviteeEmail = !!(
        inviteeEmail && user?.email && user.email.toLowerCase() === inviteeEmail.toLowerCase()
    );

    const organizationName =
        inviteDetails?.organization_name ||
        inviteMismatchInfo?.organizationName ||
        inviteDetails?.organization_id ||
        inviteMismatchInfo?.organizationId ||
        "Organização";

    const shouldOfferSwitchAccount = inviteMismatchDetected;

    return (
        <div className="flex min-h-screen items-center justify-center bg-secondary px-4 py-8 font-sans">
            <div className="flex w-full max-w-[520px] flex-col gap-6 rounded-xl border border-border bg-card p-8 shadow-sm sm:p-6">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-primary">
                    Convite para organização
                </span>

                {loadingInvite || (isLoading && !isAuthenticated && !inviteError) ? (
                    <div className="flex flex-col items-center gap-3 py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Carregando informações do convite...</span>
                    </div>
                ) : inviteError ? (
                    <div className="flex flex-col gap-3">
                        <h2 className="text-lg font-semibold tracking-tight text-foreground">
                            Não foi possível mostrar o convite
                        </h2>
                        <p className="text-sm leading-relaxed text-muted-foreground">{inviteError}</p>
                        {shouldOfferSwitchAccount && (
                            <div className="flex flex-col gap-1.5 rounded-md border border-dashed border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                                {inviteMismatchInfo?.organizationName && (
                                    <span>
                                        Convite para <strong className="text-foreground">{inviteMismatchInfo.organizationName}</strong>.
                                    </span>
                                )}
                                {inviteMismatchInfo?.inviteeEmail ? (
                                    <span>
                                        Use o e-mail <strong className="text-foreground">{inviteMismatchInfo.inviteeEmail}</strong> para continuar.
                                    </span>
                                ) : (
                                    <span>Use o e-mail do convite para continuar.</span>
                                )}
                                {user?.email && (
                                    <span>
                                        Você está autenticado como <strong className="text-foreground">{user.email}</strong>.
                                    </span>
                                )}
                            </div>
                        )}
                        {shouldOfferSwitchAccount ? (
                            <div className="flex flex-wrap gap-3">
                                <Button onClick={handleSwitchAccount}>
                                    {inviteMismatchInfo?.inviteeEmail
                                        ? `Entrar como ${inviteMismatchInfo.inviteeEmail}`
                                        : "Entrar com outra conta"}
                                </Button>
                            </div>
                        ) : (
                            showLoginPrompt && (
                                <div className="flex flex-wrap gap-3">
                                    <Button onClick={handleLogin}>Fazer login</Button>
                                </div>
                            )
                        )}
                    </div>
                ) : !inviteDetails ? (
                    <div className="flex flex-col gap-3">
                        <h2 className="text-lg font-semibold tracking-tight text-foreground">Convite não disponível</h2>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                            Não encontramos os detalhes deste convite. Solicite um novo convite ao administrador da organização.
                        </p>
                    </div>
                ) : (
                    <>
                        <h1 className="text-xl font-bold tracking-tight text-foreground">
                            Você foi convidado para {organizationName}
                        </h1>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                            {isAuthenticatedWithInviteeEmail ? (
                                <>
                                    Você está autenticado como <strong className="text-foreground">{inviteDetails.invitee_email}</strong>. Revise as
                                    informações abaixo e continue para aceitar ou recusar este convite.
                                </>
                            ) : (
                                <>
                                    Faça login com <strong className="text-foreground">{inviteDetails.invitee_email}</strong> para aceitar ou recusar
                                    este convite.
                                </>
                            )}
                        </p>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/30 p-3.5">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Responsável pelo convite
                                </span>
                                <span className="text-sm font-semibold text-foreground">
                                    {inviteDetails.invited_by_email ?? "Não informado"}
                                </span>
                            </div>
                            <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/30 p-3.5">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Papel ao entrar
                                </span>
                                <span className="text-sm font-semibold text-foreground">
                                    {getRoleDisplay(inviteDetails.role)}
                                </span>
                            </div>
                            <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/30 p-3.5">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Expira em
                                </span>
                                <span className="text-sm font-semibold text-foreground">
                                    {formatLocalizedDateTime(inviteDetails.expires_at)}
                                </span>
                            </div>
                        </div>

                        {actionCompleted ? (
                            <div className="flex flex-col gap-3">
                                <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                    {actionCompleted === "accept" ? "Convite aceito!" : "Convite recusado"}
                                </h2>
                                <p className="text-sm leading-relaxed text-muted-foreground">{actionStatus}</p>
                                <div className="flex flex-wrap gap-3">
                                    <Button onClick={handleGoToPlatform}>Ir para a plataforma</Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-3">
                                <Button
                                    onClick={() => handleAction("accept")}
                                    disabled={processingAction}
                                >
                                    {processingAction ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Processando...
                                        </>
                                    ) : (
                                        "Aceitar convite"
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => handleAction("decline")}
                                    disabled={processingAction}
                                >
                                    Recusar convite
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
            <ToastContainer />
        </div>
    );
}

export default OrganizationInvitePage;
